import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LoadingButton,
  Typography,
} from '@tempmee/react';
import { removePromoCodeErrorMessage, useRemoveOfficePromoCode } from '../../../../domain/Campaigns';

/**
 * The confirm step before taking an applied code back from the office
 * (GOTU-9821). Mounted only for the row being removed.
 *
 * Removing is idempotent on the service — already removed and never held are
 * the same 204 — so a stale row confirms cleanly rather than erroring. A real
 * failure (a missing permission, say) shows plain copy for the service's
 * `reason` (see `promoCodeErrors.js`) and keeps the dialog open.
 */
export const RemovePromoCodeDialog = ({ officeId, row, onClose }) => {
  const [submitError, setSubmitError] = useState(null);

  const removeCode = useRemoveOfficePromoCode(officeId);

  const handleConfirm = () => {
    setSubmitError(null);
    // Per-call callbacks, so a request that settles after the dialog was
    // closed does not close (or error) whatever is open by then.
    removeCode.mutate(row.campaignId, {
      onSuccess: onClose,
      onError: (error) => setSubmitError(removePromoCodeErrorMessage(error)),
    });
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Remove promo code</DialogTitle>

      <DialogContent>
        <Column gap={4}>
          <Typography variant="body1">
            {`Are you sure you want to remove ${row.codeLabel} (${row.campaignName})`}
            {' from this office?'}
          </Typography>

          {submitError ? (
            <Typography role="alert" variant="body2" className="text-error">
              {submitError}
            </Typography>
          ) : null}
        </Column>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton color="error" loading={removeCode.isPending} onClick={handleConfirm}>
          Remove
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

RemovePromoCodeDialog.propTypes = {
  officeId: PropTypes.string.isRequired,
  row: PropTypes.shape({
    campaignId: PropTypes.string.isRequired,
    campaignName: PropTypes.string.isRequired,
    codeLabel: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RemovePromoCodeDialog;
